import type { ReactNode } from 'react';

/**
 * One table component, responsive by construction.
 *
 * ## Why not just let it scroll
 *
 * A seven-column payouts table inside a 390px viewport is either a page that
 * scrolls sideways — which hides the amount column, the one column that
 * matters — or a horizontal scroller nobody discovers. Under 640px the CSS
 * turns every row into a card and every cell into a labelled line, and the
 * label comes from `data-label`, which is why `header` is typed as a string:
 * an element cannot become the content of a `::before`.
 *
 * ## Why it stays a real `<table>`
 *
 * The stacking is `display: block` on the existing elements, not a different
 * DOM. A screen reader still gets a table, `getByRole('table')` still finds
 * one, and the header cells still name the columns — the layout changes, the
 * semantics do not.
 *
 * ## The primary column
 *
 * One column per table is marked `primary`. It renders as `<th scope="row">`,
 * so every data cell has a row header to be announced against, and on a phone
 * it becomes the stacked card's title with a rule under it.
 *
 * ## …except when the table is small enough to stay a table
 *
 * `compact` is the exception, and it is narrow on purpose: a key and two or
 * three short figures — the Wallet's day / earned / paid out — reads fine as a
 * real table at 390px and is WORSE as cards, because the whole point of that
 * table is comparing a column of money down the page. Stacked, ten days came
 * to about 3,000px of scrolling to show twenty numbers. It is opt-in per
 * table rather than a column count, because "three columns" is not the test —
 * "three columns of short values" is, and only the caller knows.
 *
 * ## The column count is a style, not a decoration
 *
 * Every table writes `--table-cols` so the stylesheet can stop it growing past
 * what its columns are worth on a wide screen. See `--table-col` in
 * `styles.css`.
 */
export interface Column<T> {
  key: string;
  /** Also the label each cell carries on a phone, so it must be a string. */
  header: string;
  cell: (row: T) => ReactNode;
  align?: 'start' | 'end';
  /** Tabular figures and no wrapping: money, counts, ranks. */
  numeric?: boolean;
  /** The one column that names the row. Exactly one per table. */
  primary?: boolean;
}

export function DataTable<T>({
  columns,
  rows,
  getKey,
  caption,
  empty,
  footer,
  isHighlighted,
  compact = false,
}: {
  columns: Array<Column<T>>;
  rows: readonly T[];
  getKey: (row: T, index: number) => string;
  /** What the table is, when the heading above it does not already say. */
  caption?: ReactNode;
  /** Rendered instead of the table when there are no rows. */
  empty: ReactNode;
  footer?: ReactNode;
  isHighlighted?: (row: T) => boolean;
  /**
   * Stay a real table on a phone instead of becoming a card per row.
   *
   * Only for a table that is a key and two or three SHORT figures. Anything
   * with a name, a link, a status and a reason in it does not fit 390px as a
   * table and must not ask for this.
   */
  compact?: boolean;
}): React.JSX.Element {
  if (rows.length === 0) return <>{empty}</>;

  return (
    <div className="table-wrap">
      <table
        className={compact ? 'data compact' : 'data'}
        style={{ '--table-cols': columns.length } as React.CSSProperties}
      >
        {caption && <caption>{caption}</caption>}
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} scope="col" data-align={column.align}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={getKey(row, index)} data-highlight={isHighlighted?.(row) ? 'true' : undefined}>
              {columns.map((column) =>
                column.primary ? (
                  <th key={column.key} scope="row" data-align={column.align}>
                    {column.cell(row)}
                  </th>
                ) : (
                  <td
                    key={column.key}
                    data-label={column.header}
                    data-align={column.align}
                    data-numeric={column.numeric ? 'true' : undefined}
                  >
                    {column.cell(row)}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
        {footer && <tfoot>{footer}</tfoot>}
      </table>
    </div>
  );
}

/** The muted second line inside a cell: an email under a name, a reason under a status. */
export function CellNote({ children }: { children: ReactNode }): React.JSX.Element {
  return <span className="cell-note">{children}</span>;
}
